import { Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import {
  ApiErrorStatusResponse,
  EmailDto,
  MobileNumberDto,
  UserAvatarFileDto,
  UserEmailTokenVaultActions,
  UserIdentityDto,
  UserSmsTokenVaultActions,
} from '@workspace/data-types';
import {
  AlertSnackBarService,
  AuraContactInfosEmailEditComponent,
  AuraContactInfosPhoneEditComponent,
  AuraPersonAvatarEditComponent,
  AuraPersonIdentityEditComponent,
  CodeVerificationDialogAuraComponent,
  CodeVerificationDialogData,
  EnumsNiceLabelUtils,
  UserTwoFAVerificationDialogAuraComponent,
} from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, filter, map, Observable, switchMap, tap } from 'rxjs';

export interface CodeVerificationResult {
  email: string;
}

@Component({
  selector: 'core-user-personal-informations',
  templateUrl: './user-personal-informations.component.html',
  imports: [
    AuraContactInfosPhoneEditComponent,
    AuraPersonIdentityEditComponent,
    AuraPersonAvatarEditComponent,
    AuraContactInfosEmailEditComponent,
  ],
  providers: [],
})
export class UserPersonnalInformationCoreComponent {
  private destroyRef = inject(DestroyRef);
  private userStore = inject(UserStore);
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private alertSnackbar = inject(AlertSnackBarService);

  title = input<string>();
  isLoading = true;
  currentUser = this.userStore.currentUser;

  isEditingIdentityNames = signal<boolean>(false);
  isEditingIdentityDemographics = signal<boolean>(false);
  isEditingAvatar = signal<boolean>(false);
  isEditingEmail = signal<boolean>(false);
  isEditingMobileNumber = signal<boolean>(false);
  isAllowedToEditEmail = signal<boolean>(false);
  isAllowedToEditMobile = signal<boolean>(false);

  constructor() {
    this.isLoading = false;

    effect(() => {
      if (this.isEditingEmail() && this.isAllowedToEditEmail() === false) {
        this.openTwoFADialog(UserEmailTokenVaultActions.USER_TWO_FA_CHANGE_EMAIL)
          .pipe(
            tap((success) => {
              if (success) {
                this.isAllowedToEditEmail.set(true);
              } else {
                this.isEditingEmail.set(false);
                this.isAllowedToEditEmail.set(false);
              }
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
      if (this.isEditingMobileNumber() && this.isAllowedToEditMobile() === false) {
        this.openTwoFADialog(UserSmsTokenVaultActions.USER_TWO_FA_CHANGE_MOBILE)
          .pipe(
            tap((success) => {
              if (success) {
                this.isAllowedToEditMobile.set(true);
              } else {
                this.isEditingMobileNumber.set(false);
                this.isAllowedToEditMobile.set(false);
              }
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    });
  }

  saveIdentityNames(identity: UserIdentityDto) {
    this.api
      .user()
      .patch()
      .identityNames({ firstName: identity.firstName, lastName: identity.lastName })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userIdentity: UserIdentityDto) => {
          if (userIdentity) {
            this.userStore.updateCurrentUserIdentity(userIdentity);
            this.alertSnackbar.success({
              title: 'Modification de votre identité',
              message: 'Votre nom et prénom ont été modifiés avec succès',
            });
            this.isEditingIdentityNames.set(false);
          }
        }),
      )
      .subscribe();
  }

  saveIdentityDemographics(identity: UserIdentityDto) {
    this.api
      .user()
      .patch()
      .identityDemographics({ birthDate: identity.birthDate, gender: identity.gender })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userIdentity: UserIdentityDto) => {
          if (userIdentity) {
            this.userStore.updateCurrentUserIdentity(userIdentity);
            this.alertSnackbar.success({
              title: 'Modification de votre identité',
              message: 'Vos informations démographiques ont été modifiées avec succès',
            });
            this.isEditingIdentityDemographics.set(false);
          }
        }),
      )
      .subscribe();
  }

  saveAvatar(avatar: File) {
    this.api
      .user()
      .patch()
      .identityAvatar(avatar)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((userIdentity: UserAvatarFileDto | undefined) => {
          this.userStore.updateCurrentUserIdentityAvatar(userIdentity);
          this.alertSnackbar.success({
            title: 'Modification de votre avatar',
            message: "L'avatar de votre utilisateur a été modifié avec succès",
          });
          this.isEditingAvatar.set(false);
        }),
      )
      .subscribe();
  }

  saveContactMobileNumber(dto: MobileNumberDto): void {
    const data: CodeVerificationDialogData<UserSmsTokenVaultActions.USER_CHANGE_MOBILE> = {
      title: 'Vérification du numéro de téléphone',
      message: `<div>Un code de vérification a été envoyé à votre nouveau numéro <strong>${dto.mobileNumber}</strong>.</div>`,
      action: UserSmsTokenVaultActions.USER_CHANGE_MOBILE,
      onComplete: (params) => this.api.tokenVaults().users().change().mobileNumber(params),
      onResend: () => this.api.user().patch().mobileNumber({ mobileNumber: dto.mobileNumber }),
    };

    data
      .onResend()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((success: boolean) => success),
        switchMap(() =>
          this.dialog
            .open<
              CodeVerificationDialogAuraComponent<UserSmsTokenVaultActions.USER_CHANGE_MOBILE>,
              CodeVerificationDialogData<UserSmsTokenVaultActions.USER_CHANGE_MOBILE>,
              CodeVerificationResult
            >(CodeVerificationDialogAuraComponent, { data })
            .afterClosed(),
        ),
        filter((result): result is CodeVerificationResult => !!result),
        tap((result) => {
          this.userStore.updateCurrentUserContactEmail(result);
          this.alertSnackbar.success({
            title: 'Modification de votre numéro de téléphone',
            message: 'Votre numéro de téléphone a été modifié avec succès',
          });
          this.isEditingMobileNumber.set(false);
        }),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          this.alertSnackbar.error({
            message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
            title: "Une erreur s'est produite",
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  saveContactEmail(dto: EmailDto): void {
    const data: CodeVerificationDialogData<UserEmailTokenVaultActions.USER_CHANGE_EMAIL> = {
      title: "Vérification de l'adresse mail",
      message: `<div>Un code de vérification a été envoyé à votre nouvelle adresse mail <strong>${dto.email}</strong>.</div>`,
      action: UserEmailTokenVaultActions.USER_CHANGE_EMAIL,
      onComplete: (params) => this.api.tokenVaults().users().change().email(params),
      onResend: () => this.api.user().patch().email({ email: dto.email }),
    };

    data
      .onResend()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((success: boolean) => success),
        switchMap(() =>
          this.dialog
            .open<
              CodeVerificationDialogAuraComponent<UserEmailTokenVaultActions.USER_CHANGE_EMAIL>,
              CodeVerificationDialogData<UserEmailTokenVaultActions.USER_CHANGE_EMAIL>,
              CodeVerificationResult
            >(CodeVerificationDialogAuraComponent, { data })
            .afterClosed(),
        ),
        filter((result): result is CodeVerificationResult => !!result),
        tap((result) => {
          this.userStore.updateCurrentUserContactEmail(result);
          this.alertSnackbar.success({
            title: 'Modification de votre email',
            message: 'Votre email a été modifié avec succès',
          });
          this.isEditingEmail.set(false);
        }),
        catchError((errorResponse) => {
          const apiError = errorResponse.error as ApiErrorStatusResponse;
          this.alertSnackbar.error({
            message: EnumsNiceLabelUtils.erroCode(apiError.statusCode, apiError.code),
            title: "Une erreur s'est produite",
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private openTwoFADialog(
    action:
      | UserEmailTokenVaultActions.USER_TWO_FA_CHANGE_EMAIL
      | UserSmsTokenVaultActions.USER_TWO_FA_CHANGE_MOBILE,
  ): Observable<boolean> {
    return this.dialog
      .open(UserTwoFAVerificationDialogAuraComponent, {
        data: { action },
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        map((success) => !!success),
        takeUntilDestroyed(this.destroyRef),
      );
  }
}
